import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../../api';
import { useLanguage } from '../../../context/LanguageContext';
import styles from '../NewUserWizard.module.css';

const GoogleLogo = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const CheckIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const StepConnect = ({ token, alreadyConnected, onNext }) => {
  const { t } = useLanguage();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Reset connecting state on mount, when window regains focus, on pageshow (e.g. browser back button from Google), or on tab visibility change
  useEffect(() => {
    const handleReset = () => {
      setConnecting(false);
    };

    setConnecting(false);

    window.addEventListener('pageshow', handleReset);
    window.addEventListener('focus', handleReset);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        handleReset();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('pageshow', handleReset);
      window.removeEventListener('focus', handleReset);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const permissions = [
    t('step2Perm1', 'View your Google Business Profile locations'),
    t('step2Perm2', 'Read all customer reviews on your profile'),
    t('step2Perm3', 'Post replies to reviews on your behalf'),
    t('step2Perm4', 'View basic account info (name & email)'),
  ];

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      localStorage.setItem('wizard_open', 'true');

      const res = await fetch(getApiUrl('/api/auth/google/url'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Server error (${res.status})`);
      }
      const { url } = await res.json();
      window.location.href = url;

      // Safety timeout: if navigation is delayed, blocked, or user quickly returns
      setTimeout(() => {
        setConnecting(false);
      }, 2500);
    } catch (err) {
      setError(err.message || 'Failed to initiate Google connection. Please try again.');
      setConnecting(false);
    }
  };

  return (
    <div>
      <div className={styles.stepTag}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
        {t('wizardStepOf', 'Step 2 of 5').replace('{current}', '2').replace('{total}', '5')}
      </div>

      <h2 className={styles.wizardTitle}>{t('step2ConnectTitle', 'Connect your Google Business Profile')}</h2>
      <p className={styles.wizardSubtitle}>
        {t('step2ConnectSub', 'We need permission to read your reviews and post replies on your behalf.')}
      </p>

      {alreadyConnected ? (
        <>
          <div className={styles.connectedCard}>
            <div className={styles.connectedCardIcon}>
              <CheckIcon size={18} />
            </div>
            <div>
              <div className={styles.connectedCardTitle}>{t('step2AlreadyConnected', 'Google account already connected!')}</div>
              <div className={styles.connectedCardSub}>{t('step2AlreadyConnectedSub', "You're ready to proceed to the next step.")}</div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className={styles.btnPrimary} onClick={onNext}>
              {t('step2ContinueBtn', 'Continue')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
              </svg>
            </button>
          </div>
        </>
      ) : (
        <div className={styles.connectLayout}>
          {/* Left: permissions + button */}
          <div className={styles.connectLeft}>
            <ul className={styles.permList}>
              {permissions.map((p, i) => (
                <li key={i} className={styles.permItem}>
                  <span className={styles.permCheck}>
                    <CheckIcon size={11} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>

            {error && (
              <div className={styles.errorAlert} role="alert">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {error}
              </div>
            )}

            <button
              type="button"
              className={styles.connectBtn}
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? (
                <>
                  <div className={`${styles.spinnerSm} ${styles.spinnerSmWhite}`} style={{ borderColor: 'rgba(0,0,0,0.15)', borderTopColor: '#374151' }} />
                  {t('step2Connecting', 'Redirecting to Google…')}
                </>
              ) : (
                <>
                  <GoogleLogo size={20} />
                  {t('step2ConnectBtn', 'Connect Google Business Profile')}
                </>
              )}
            </button>

            <div className={styles.securityNote}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              {t('step2SecurityNote', 'Your credentials are never stored in plain text. Only encrypted OAuth tokens.')}
            </div>
          </div>

          {/* Right: mock Google consent preview */}
          <div className={styles.connectRight}>
            <div className={styles.mockConsentCard}>
              <div className={styles.mockConsentHeader}>
                <GoogleLogo size={18} />
                {t('step2ConsentHeader', 'Google')}
              </div>
              <div className={styles.mockConsentTitle}>
                {t('step2ConsentTitle', 'ReplyShoot wants access to your Google Account')}
              </div>
              <ul className={styles.mockConsentList}>
                <li>{t('step2ConsentPerm1', 'View & manage Business Profile')}</li>
                <li>{t('step2ConsentPerm2', 'Read and respond to reviews')}</li>
                <li>{t('step2ConsentPerm3', 'View account info')}</li>
              </ul>
              <div className={styles.mockConsentBtns}>
                <span className={styles.mockDenyBtn}>{t('step2ConsentDeny', 'Deny')}</span>
                <span className={styles.mockAllowBtn}>{t('step2ConsentAllow', 'Allow')}</span>
              </div>
            </div>
            <p style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 10, lineHeight: 1.5 }}>
              {t('step2ConsentHint', "↑ This is the Google screen you'll see. Click Allow to proceed.")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StepConnect;
